/**
 * 统一配置管理
 * 通过修改 DEPLOYMENT_TARGET 可以快速切换本地和线上环境
 */

// 🎯 核心配置：修改这里可以快速切换环境
const DEPLOYMENT_TARGET = 'production'; // 'development' | 'production' | 'local'
//const DEPLOYMENT_TARGET = 'development'; // 'development' | 'production' | 'local'

// 环境配置定义
const environments = {
  // 本地开发环境
  development: {
    name: '本地开发',
    api: {
      baseURL: 'http://localhost:8000/api/v1',
      wsURL: 'ws://localhost:8000/api/v1',
      timeout: 10000
    },
    features: {
      debug: true,
      mockData: false,
      analytics: false
    }
  },
  
  // 本地测试环境（连接远程服务器）
  local: {
    name: '本地测试',
    api: {
      baseURL: 'http://129.211.162.161:8000/api/v1',
      wsURL: 'ws://129.211.162.161:8000/api/v1',
      timeout: 15000
    },
    features: {
      debug: true,
      mockData: false,
      analytics: false
    }
  },
  
  // 生产环境
  production: {
    name: '生产环境',
    api: {
      // 使用相对路径，由Nginx代理到后端
      // Nginx配置: location /api/ -> proxy_pass http://129.211.162.161:8000
      // WebSocket配置: location /api/v1/data-processing/ws/ -> proxy_pass http://127.0.0.1:8000
      baseURL: '/api/v1',
      // WebSocket URL会在运行时根据当前协议动态生成（HTTP -> ws://, HTTPS -> wss://）
      wsURL: '/api/v1',
      timeout: 30000
    },
    features: {
      debug: false,
      mockData: false,
      analytics: true
    }
  },
  
};

// 获取当前环境配置
const getCurrentEnvironment = () => {
  // 优先使用代码中设置的 DEPLOYMENT_TARGET
  let target = DEPLOYMENT_TARGET;
  
  // 如果有环境变量，则使用环境变量（用于CI/CD）
  if (process.env.REACT_APP_DEPLOYMENT_TARGET) {
    target = process.env.REACT_APP_DEPLOYMENT_TARGET;
  }
  
  // 如果有URL参数，则使用URL参数（用于调试）
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('env')) {
    target = urlParams.get('env');
  }
  
  return environments[target] || environments.production;
};

// 当前配置
const config = getCurrentEnvironment();

// 导出配置
export default {
  // 环境信息
  environment: {
    name: config.name,
    isDevelopment: DEPLOYMENT_TARGET === 'development',
    isProduction: DEPLOYMENT_TARGET === 'production',
    isLocal: DEPLOYMENT_TARGET === 'local'
  },
  
  // API配置
  api: {
    baseURL: config.api.baseURL,
    // WebSocket URL：如果是相对路径，则动态生成完整URL
    wsURL: (() => {
      const wsURL = config.api.wsURL;
      // 如果是相对路径（以/开头），则根据当前协议和主机动态生成
      if (wsURL.startsWith('/')) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}${wsURL}`;
      }
      return wsURL;
    })(),
    timeout: config.api.timeout,
    
    // 便捷方法
    getFullURL: (path) => {
      const baseURL = config.api.baseURL;
      if (baseURL.startsWith('/')) {
        // 相对路径，使用当前协议和主机
        return `${window.location.protocol}//${window.location.host}${baseURL}${path.startsWith('/') ? path : '/' + path}`;
      }
      return `${baseURL}${path.startsWith('/') ? path : '/' + path}`;
    },
    getWSURL: (path) => {
      const wsURL = config.api.wsURL;
      // 如果是相对路径，动态生成
      if (wsURL.startsWith('/')) {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}${wsURL}${path.startsWith('/') ? path : '/' + path}`;
      }
      return `${wsURL}${path.startsWith('/') ? path : '/' + path}`;
    }
  },
  
  // 功能开关
  features: config.features,
  
  // 应用配置
  app: {
    title: 'Amazon Product Management',
    version: '1.0.0',
    description: '亚马逊产品管理系统'
  },
  
  // WebSocket配置
  websocket: {
    reconnectInterval: 5000,  // 增加重连间隔到5秒
    maxReconnectAttempts: 3,  // 减少重连次数到3次
    connectionTimeout: 10000, // 增加连接超时到10秒
    heartbeatInterval: 45000  // 增加心跳间隔到45秒
  },
  
  // 上传配置
  upload: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ]
  },
  
  // 分页配置
  pagination: {
    defaultPageSize: 20,
    pageSizeOptions: ['10', '20', '50', '100']
  }
};

// 开发环境下输出配置信息
if (config.features.debug) {
  console.log('🔧 当前配置:', {
    environment: config.name,
    apiURL: config.api.baseURL,
    wsURL: config.api.wsURL,
    features: config.features
  });
}

// 导出环境切换函数（仅开发环境）
export const switchEnvironment = (target) => {
  if (config.features.debug) {
    const url = new URL(window.location);
    url.searchParams.set('env', target);
    window.location.href = url.toString();
  }
};

// 导出所有可用环境
export const availableEnvironments = Object.keys(environments).map(key => ({
  key,
  name: environments[key].name,
  current: key === DEPLOYMENT_TARGET
}));